import * as React from "react";
import { Thermometer, ThermometerSun, Flame, TriangleAlert } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { STUFEN_INFO, type ConfirmedLevel } from "@/lib/stufen-labels";

/**
 * HeatBadge — visualisiert die ASR-A3.5-Hitzestufe einer Raum-Messung.
 *
 * SINGLE-SOURCE: alle Labels + Farb-Tokens kommen aus stufen-labels.ts.
 * level ist der DB-Wert 0..3 (ConfirmedLevel) — KEIN +1-offset.
 *
 * Color-blindness-safe (WCAG 1.4.1): niemals Farbe alleine.
 * Jede Stufe trägt IMMER:
 *   1) farbigen Hintergrund (heat-1..4 token, per DB-Stufe gemappt)
 *   2) Pflicht-Icon (visual-shield)
 *   3) Label aus STUFEN_INFO (z.B. "Kritisch") + DB-Zahl (0-3)
 *
 * Touch-target 44px+ (Apple HIG) ist via parent-Layout zu garantieren,
 * da das Badge selbst informativ (nicht interaktiv) ist.
 */

const ICON_MAP: Record<ConfirmedLevel, LucideIcon> = {
  0: Thermometer,
  1: ThermometerSun,
  2: Flame,
  3: TriangleAlert,
};

/** Deutsche Kommastelle für Temperatur-Werte ("32,5 °C").
 * Schmales geschütztes Leerzeichen (U+202F) zwischen Zahl und °C
 * (deutsche Typografie-Konvention, kein Zeilenumbruch dazwischen).
 */
const tempFormatter = new Intl.NumberFormat("de-DE", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

function formatTemp(value: number): string {
  return `${tempFormatter.format(value)} °C`;
}

export interface HeatBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children"> {
  /**
   * ASR-A3.5-Stufe als DB-Wert 0..3 (ConfirmedLevel).
   * 0=Normalbereich, 1=Soll, 2=Muss, 3=Kritisch.
   * KEIN +1-offset — direkt DB-Wert übergeben.
   */
  level: ConfirmedLevel;
  /** Temperatur in °C (optional). Wird als deutsche Kommastelle gerendert. */
  temp?: number;
  /** compact-Darstellung: kleinere Abstände + nur Zahl (kein Langname). */
  compact?: boolean;
  /** Icon sichtbar (default true). Auch wenn false bleibt aria-label semantisch. */
  showIcon?: boolean;
  className?: string;
}

export function HeatBadge({
  level,
  temp,
  compact = false,
  showIcon = true,
  className,
  ...rest
}: HeatBadgeProps): React.ReactElement {
  const info = STUFEN_INFO[level];
  const Icon = ICON_MAP[level];

  // Screen-reader-text inkl. semantischer Stufenbenennung.
  // Beispiel: "Hitzestufe 3 Kritisch, Betrieb einschränken/prüfen, 36,0 °C"
  const ariaLabel = [
    `Hitzestufe ${level} ${info.short}`,
    info.zusatz || null,
    typeof temp === "number" ? formatTemp(temp) : null,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <span
      role="status"
      aria-label={ariaLabel}
      className={cn(
        // base: pill-form, weiße Schrift, semibold, tabular-nums
        "inline-flex items-center gap-2 rounded-full",
        "font-semibold leading-none",
        "text-[color:var(--color-text-on-primary)]",
        "[font-variant-numeric:tabular-nums]",
        "transition-colors duration-150 ease-out",
        "select-none",
        compact ? "px-2 py-1 text-xs" : "px-3 py-1.5 text-sm",
        className,
      )}
      style={{ backgroundColor: `var(${info.colorToken})` }}
      {...rest}
    >
      {showIcon && (
        <Icon
          aria-hidden="true"
          strokeWidth={2.25}
          className={compact ? "size-3.5 shrink-0" : "size-4 shrink-0"}
        />
      )}
      <span aria-hidden="true">
        {compact ? (
          // compact: nur DB-Zahl (0-3)
          <>{level}</>
        ) : (
          <>
            {info.short}
            <span className="ml-1 opacity-70 [font-variant-numeric:tabular-nums]">
              ({level})
            </span>
            {typeof temp === "number" && (
              <>
                <span className="mx-1 opacity-50" aria-hidden="true">
                  ·
                </span>
                {formatTemp(temp)}
              </>
            )}
          </>
        )}
      </span>
    </span>
  );
}

HeatBadge.displayName = "HeatBadge";
